import { tabDashboardRoute } from '../data/tab_dashboard.ts';
import { currentRoute, navigate, routeState } from '../ui/route.ts';

// Moving into, around and out of the settings, which lie over the dashboard (docs/specs/settings.md,
// UI-2). In a browser at /settings and /settings/<part>, in a shell at #settings (ui/route.ts). Each
// step is a history entry, so the browser's and Android's back go back a step; how many steps the
// settings took is kept in the entry, so that closing them goes back to the dashboard entry instead
// of adding another on top.

export type SettingsPart = 'store' | 'dashboards' | 'security' | 'app' | 'drivers' | 'diagnostics';

const PARTS: readonly SettingsPart[] = ['store', 'dashboards', 'security', 'app', 'drivers', 'diagnostics'];

/** The settings' route of a page's route: null for the dashboard, '' for the settings' list, or a part. */
export function settingsRoute(route: string) : SettingsPart | '' | null {
    if (route === '/settings') {
        return '';
    }
    if (!route.startsWith('/settings/')) {
        return null;
    }

    const part = route.slice('/settings/'.length) as SettingsPart;

    return PARTS.includes(part) ? part : '';
}

function depth() : number {
    const { settingsDepth } = routeState();

    return typeof settingsDepth === 'number' ? settingsDepth : 0;
}

/** Opens the settings, or another part of them; from the start page (/), over it (`overHome`). */
export function goToSettings(part: SettingsPart | '' = '') : void {
    const overHome = routeState().overHome === true || currentRoute() === '/';

    navigate(part === '' ? '/settings' : `/settings/${part}`, { state: { settingsDepth: depth() + 1, overHome } });
}

/** Back to the dashboard, with the history as it was before the settings opened. */
export function closeSettings() : void {
    const steps = depth();
    if (steps > 0) {
        history.go(-steps);

        return;
    }

    // Opened at a settings address: back to this tab's dashboard.
    navigate(tabDashboardRoute(), { replace: true });
}

/** One step back inside the settings: to the list of parts. */
export function backInSettings() : void {
    if (depth() > 1) {
        history.back();
    } else {
        goToSettings('');
    }
}
